import { Box } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useScoutConversation } from "../hooks/useScoutConversation";
import { ChatChrome } from "./ChatChrome";
import { NewChat } from "./NewChat";

export const ScoutChat = ({
  onHasMessagesChange,
  onConversationTitleChange,
}: {
  onHasMessagesChange?: (hasMessages: boolean) => void;
  onConversationTitleChange?: (input: {
    conversationId: string | undefined;
    title: string | undefined;
  }) => void;
}) => {
  const navigate = useNavigate();
  const { tenantId, conversationId } = useParams();
  const handleConversationIdChange = useCallback(
    (nextConversationId: string) => {
      if (!tenantId) {
        return;
      }

      navigate(`/o/${tenantId}/scout/${nextConversationId}`, { replace: true });
    },
    [navigate, tenantId]
  );
  const {
    conversation,
    messages,
    activeTurn,
    loading,
    isSending,
    isAborting,
    sendMessage,
    abortTurn,
  } = useScoutConversation({
    tenantId,
    conversationId,
    onConversationIdChange: handleConversationIdChange,
  });
  const hasMessages = messages.length > 0;
  const isConversationRoute = Boolean(conversationId);
  const isLoadingConversation = isConversationRoute && loading && !hasMessages;
  useEffect(() => {
    onHasMessagesChange?.(hasMessages);
  }, [hasMessages, onHasMessagesChange]);
  useEffect(() => {
    onConversationTitleChange?.({
      conversationId: conversation?.id,
      title: conversation?.title,
    });
  }, [conversation?.id, conversation?.title, onConversationTitleChange]);
  const isTurnRunning =
    isSending ||
    isAborting ||
    activeTurn?.status === "starting" ||
    activeTurn?.status === "streaming";
  const handleSubmit = useCallback(
    async (message: string) => {
      await sendMessage(message);
    },
    [sendMessage]
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
      }}
    >
      {!hasMessages && !isConversationRoute ? (
        <NewChat
          onSubmit={handleSubmit}
          onAbort={abortTurn}
          isSending={isTurnRunning}
          isAborting={isAborting}
        />
      ) : (
        <ChatChrome
          messages={messages}
          loading={isLoadingConversation}
          initialPinned={isConversationRoute}
          onSubmit={handleSubmit}
          onAbort={abortTurn}
          isSending={isTurnRunning}
          isAborting={isAborting}
        />
      )}
    </Box>
  );
};
